import { Haptics as NativeHaptics, ImpactStyle } from '@capacitor/haptics';
import { isNative } from './native';

/**
 * Haptic feedback. On the web it uses the Vibration API (Android browsers; iOS Safari ignores it);
 * in the native apps, @capacitor/haptics (Taptic Engine / Android haptics).
 */
export interface Haptics {
  /** Subtle tick, e.g. a turn. */
  tap(): void;
  /** Noticeable bump, e.g. a survivable crash. */
  impact(): void;
  /** Strong, e.g. the mower breaking. */
  heavy(): void;
}

function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers throw when called without a prior user gesture; haptics are optional.
  }
}

export const webHaptics: Haptics = {
  tap: () => vibrate(8),
  impact: () => vibrate(30),
  heavy: () => vibrate([40, 30, 70]),
};

const impact = (style: ImpactStyle) => () => {
  NativeHaptics.impact({ style }).catch(() => undefined);
};

export const nativeHaptics: Haptics = {
  tap: impact(ImpactStyle.Light),
  impact: impact(ImpactStyle.Medium),
  heavy: impact(ImpactStyle.Heavy),
};

let enabled = true;
let device: Haptics | null = null;
const active = () => (device ??= isNative() ? nativeHaptics : webHaptics);

/** The active haptics, honoring the player's vibration setting. */
export const haptics: Haptics & { setEnabled(on: boolean): void } = {
  tap: () => enabled && active().tap(),
  impact: () => enabled && active().impact(),
  heavy: () => enabled && active().heavy(),
  setEnabled: (on) => {
    enabled = on;
  },
};
